import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  MEMORY_CONTENT_MAX,
  MEMORY_KINDS,
  type Memory,
  type MemoryInput,
  type MemoryKind,
  type MemoryScope,
  type MemoryValidity,
} from "../types/MemoryTypes";
import { isValidCustomDate, validityChanged, validityOf } from "../utils/memoryView";

const VALIDITIES: MemoryValidity[] = ["permanent", "1d", "7d", "30d", "custom"];

interface MemoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sem memória, cria; com ela, edita. `renew` abre direto na validade. */
  memory?: Memory | null;
  renew?: boolean;
  /** Na edição, para onde a memória pode ir; com um escopo só, o campo não aparece. */
  scopes?: MemoryScope[];
  saving: boolean;
  onSubmit: (input: MemoryInput) => void;
}

/** Criar, editar e renovar uma memória (AB#87149). */
export const MemoryFormDialog = ({ open, onOpenChange, memory, renew, scopes = [], saving, onSubmit }: MemoryFormDialogProps) => {
  const { t } = useTranslation();
  const [content, setContent] = useState("");
  const [kind, setKind] = useState<MemoryKind>("fact");
  const [scope, setScope] = useState<MemoryScope | undefined>(undefined);
  const canMove = !!memory && !renew && scopes.length > 1;
  const [validity, setValidity] = useState<MemoryValidity>("permanent");
  const [customDate, setCustomDate] = useState<string | undefined>(undefined);
  const [initialValidity, setInitialValidity] = useState<Pick<MemoryInput, "validity" | "customDate">>({
    validity: "permanent",
  });

  useEffect(() => {
    if (!open) return;
    setContent(memory?.content ?? "");
    setKind(memory?.kind ?? "fact");
    setScope(memory?.scope);
    // Renovar uma vencida: a validade anterior já passou, então começa nos 7 dias.
    const current = memory && !renew ? validityOf(memory) : { validity: renew ? "7d" : "permanent" };
    const initial = {
      validity: current.validity as MemoryValidity,
      customDate: "customDate" in current ? current.customDate : undefined,
    };
    setValidity(initial.validity);
    setCustomDate(initial.customDate);
    setInitialValidity(initial);
  }, [open, memory, renew]);

  // Editar sem mexer na validade não a reenvia; renovar sempre envia a nova.
  const editingOnly = !!memory && !renew;

  const dateInvalid = validity === "custom" && !isValidCustomDate(customDate);
  const canSave = content.trim().length > 0 && content.length <= MEMORY_CONTENT_MAX && !dateInvalid && !saving;
  const title = renew ? "memory.form.renewTitle" : memory ? "memory.form.editTitle" : "memory.form.createTitle";

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(title)}</DialogTitle>
          <DialogDescription>{t("memory.form.validityHint")}</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSave) return;
            onSubmit({
              content: content.trim(),
              kind,
              validity,
              customDate,
              ...(canMove ? { scope } : {}),
              ...(editingOnly ? { validityChanged: validityChanged(initialValidity, { validity, customDate }) } : {}),
            });
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="memory-content">{t("memory.form.content")}</Label>
            <Textarea
              id="memory-content"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder={t("memory.form.contentPlaceholder")}
              rows={4}
              maxLength={MEMORY_CONTENT_MAX}
              disabled={renew}
            />
            <p className="text-right text-xs text-muted-foreground">
              {content.length}/{MEMORY_CONTENT_MAX}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("memory.form.kind")}</Label>
              <Select value={kind} onValueChange={(value) => setKind(value as MemoryKind)} disabled={renew}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEMORY_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`memory.kinds.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("memory.form.validity")}</Label>
              <Select value={validity} onValueChange={(value) => setValidity(value as MemoryValidity)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VALIDITIES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`memory.form.validityOptions.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {canMove && (
            <div className="space-y-1.5">
              <Label>{t("memory.form.scope")}</Label>
              <Select value={scope} onValueChange={(value) => setScope(value as MemoryScope)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scopes.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`memory.form.scopeOptions.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {scope && scope !== memory?.scope && (
                <p className="text-xs text-muted-foreground">{t("memory.form.scopeHint")}</p>
              )}
            </div>
          )}

          {validity === "custom" && (
            <div className="space-y-1.5">
              <Label htmlFor="memory-date">{t("memory.form.date")}</Label>
              <Input
                id="memory-date"
                type="date"
                value={customDate ?? ""}
                onChange={(event) => setCustomDate(event.target.value || undefined)}
              />
              {dateInvalid && <p className="text-xs text-destructive">{t("memory.form.invalidDate")}</p>}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              {t("memory.form.cancel")}
            </Button>
            <Button type="submit" disabled={!canSave}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("memory.form.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
